import React, { useState, useEffect } from "react"
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet"
import { Box, Typography, Button, Chip } from "@mui/material"
import { LocationOn, Star } from "@mui/icons-material"
import L from "leaflet"
import { formatDistance } from "../utils/distanceUtils"

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
})

// Custom bathroom marker icon - traditional pin style with toilet emoji
const bathroomIcon = L.divIcon({
  html: `<div style="
    position: relative;
    width: 30px;
    height: 40px;
  ">
    <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C8.373 0 3 5.373 3 12c0 9 12 28 12 28s12-19 12-28c0-6.627-5.373-12-12-12z"
            fill="#1976d2" stroke="white" stroke-width="2"/>
    </svg>
    <div style="
      position: absolute;
      top: 6px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 16px;
      line-height: 1;
    ">🚽</div>
  </div>`,
  className: "custom-emoji-icon",
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -40],
})

// User location marker icon
const userIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#f44336" width="24" height="24">
      <circle cx="12" cy="12" r="8" fill="#f44336"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
})

// Component to update map view when center changes
const MapUpdater = ({ center, zoom }) => {
  const map = useMap()

  useEffect(() => {
    if (center) {
      map.setView(center, zoom)
    }
  }, [center, zoom, map])

  return null
}

const BathroomMap = ({
  bathrooms = [],
  center = [40.7128, -74.006],
  zoom = 13,
  userLocation = null,
  onBathroomClick = () => {},
  height = "400px",
}) => {
  const [mapCenter, setMapCenter] = useState(center)
  const [mapZoom, setMapZoom] = useState(zoom)

  useEffect(() => {
    if (userLocation) {
      setMapCenter([userLocation.latitude, userLocation.longitude])
      setMapZoom(14)
    }
  }, [userLocation])

  const handleBathroomClick = bathroom => {
    onBathroomClick(bathroom)
  }

  return (
    <Box sx={{ height, width: "100%", borderRadius: 1, overflow: "hidden" }}>
      <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
        <MapUpdater center={mapCenter} zoom={mapZoom} />

        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* User location marker */}
        {userLocation && (
          <Marker position={[userLocation.latitude, userLocation.longitude]} icon={userIcon}>
            <Popup>
              <Typography variant="body2" fontWeight="bold">
                Your Location
              </Typography>
            </Popup>
          </Marker>
        )}

        {/* Bathroom markers */}
        {bathrooms.map(bathroom => {
          if (!bathroom.latitude || !bathroom.longitude) return null

          return (
            <Marker key={bathroom.id} position={[bathroom.latitude, bathroom.longitude]} icon={bathroomIcon}>
              <Popup>
                <Box sx={{ minWidth: 200, maxWidth: 300 }}>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {bathroom.name || "Unnamed Location"}
                  </Typography>

                  {bathroom.address && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <LocationOn fontSize="small" color="action" sx={{ mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {bathroom.address}
                      </Typography>
                    </Box>
                  )}

                  {bathroom.ratings?.overall && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <Star fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                      <Typography variant="body2">
                        {bathroom.ratings.overall.toFixed(1)} ({bathroom.reviewCount || 0} reviews)
                      </Typography>
                    </Box>
                  )}

                  {bathroom.distance !== undefined && (
                    <Chip size="small" label={formatDistance(bathroom.distance)} color="primary" variant="outlined" sx={{ mb: 1 }} />
                  )}

                  <Button variant="contained" size="small" fullWidth onClick={() => handleBathroomClick(bathroom)} sx={{ mt: 1 }}>
                    View Details
                  </Button>
                </Box>
              </Popup>
            </Marker>
          )
        })}
      </MapContainer>
    </Box>
  )
}

export default BathroomMap
